import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SessionStorageService } from '@services/session-storage/session-storage.service';
import { SupportTierService } from '@services/support-tier/support-tier.service';

const SESSION_KEY_PREFIX = 'support_tier_trial_banner_dismissed_';

/**
 * Topbar banner for the whole of a support tier trial: which tier the account is trialling and until when, with a link to the
 * support tier page, for the users who can open that page. Dismissal is per session and keyed on the end date, so an extended
 * trial shows again.
 */
@Component({
    selector: 'app-support-tier-trial-banner',
    imports: [RouterLink, TranslateModule],
    templateUrl: './support-tier-trial-banner.component.html',
    styleUrl: './support-tier-trial-banner.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupportTierTrialBannerComponent {
    private readonly dismissedEnds = signal<ReadonlySet<number>>(new Set());

    readonly trial = this.supportTierService.trial;
    readonly visible = computed(() => {
        const trial = this.trial();
        if (!trial || !this.supportTierService.canManage()) return false;
        return !this.dismissedEnds().has(trial.endUnix) && !this.sessionStorageService.getItem(`${SESSION_KEY_PREFIX}${trial.endUnix}`);
    });

    constructor(
        private readonly supportTierService: SupportTierService,
        private readonly sessionStorageService: SessionStorageService
    ) {}

    dismiss(): void {
        const trial = this.trial();
        if (!trial) return;
        this.sessionStorageService.setItem(`${SESSION_KEY_PREFIX}${trial.endUnix}`, 'true');
        this.dismissedEnds.update(ends => new Set(ends).add(trial.endUnix));
    }
}
